/**
 * SMS reminder consent — shared frontend constants (issue #4071).
 *
 * Single source of truth for the consent status vocabulary, the 4-state chip appearance,
 * the verbal capture channel options, and the disclosure script. The disclosure text must
 * stay in sync with the backend App\Constants\SmsConsentDisclosure::TEMPLATE (a future
 * shared read-endpoint can collapse the two; FR-6).
 */

// ContactPoint-smsReminderConsent.status codes.
export const SMS_CONSENT_STATUS = {
    NOT_ASKED: 'not-asked',
    OPTED_IN: 'opted-in',
    DECLINED: 'declined',
    OPTED_OUT_CARRIER: 'opted-out-carrier',
};

// Capture methods (mirrors App\Enum\PatientConsentMethod).
export const SMS_CONSENT_METHOD = {
    VERBAL: 'verbal',
    DIGITAL_FORM: 'digital-form',
    SMS_KEYWORD: 'sms-keyword',
    STAFF_REVOKED: 'staff-revoked',
};

// Human-readable labels for capture methods (sentence case). Used wherever a method code
// would otherwise be shown to users (chip popover, form summary).
export const SMS_CONSENT_METHOD_LABELS = {
    [SMS_CONSENT_METHOD.VERBAL]: 'verbal',
    [SMS_CONSENT_METHOD.DIGITAL_FORM]: 'online form',
    [SMS_CONSENT_METHOD.SMS_KEYWORD]: 'text message',
    [SMS_CONSENT_METHOD.STAFF_REVOKED]: 'staff update',
};

// Recorded actions (mirrors App\Enum\PatientConsentAction).
export const SMS_CONSENT_ACTION = {
    GRANTED: 'granted',
    DECLINED: 'declined',
    REVOKED: 'revoked',
    REINSTATED: 'reinstated',
};

// Verbal capture channels (mirrors App\Enum\PatientConsentChannel; verbal subset).
export const SMS_CONSENT_CHANNELS = [
    { value: 'in_person', label: 'In person' },
    { value: 'phone_call', label: 'Phone call' },
];

/**
 * Per-status chip appearance. Colors follow the design system: opted-in = success,
 * opted-out = error, not-asked / declined = neutral grey.
 */
export const SMS_CONSENT_CHIP_CONFIG = {
    [SMS_CONSENT_STATUS.NOT_ASKED]: {
        label: 'Not asked',
        color: 'grey-lighten-1',
        icon: 'question-circle',
    },
    [SMS_CONSENT_STATUS.OPTED_IN]: {
        label: 'Opted in',
        color: 'success',
        icon: 'check-circle',
    },
    [SMS_CONSENT_STATUS.DECLINED]: {
        label: 'Declined',
        color: 'grey',
        icon: 'minus-circle',
    },
    [SMS_CONSENT_STATUS.OPTED_OUT_CARRIER]: {
        label: 'Opted out',
        color: 'error',
        icon: 'times-circle',
    },
};

// Disclosure script. Keep in sync with App\Constants\SmsConsentDisclosure::TEMPLATE.
export const SMS_CONSENT_DISCLOSURE_TEMPLATE =
    '{clinic_name} may send you text messages including appointment reminders and clinical ' +
    'communications from your care team. Message frequency varies. Message and data rates may ' +
    'apply. Reply STOP at any time to opt out. Reply HELP for options. You can find our mobile ' +
    'terms and privacy policy online at {clinic_website}. Do you consent?';

// The terms/privacy sentence of the template. Mirrors SmsConsentDisclosure::WEBSITE_SENTENCE —
// omitted entirely (no dangling placeholder, no double space) when no website is known.
const SMS_CONSENT_WEBSITE_SENTENCE = 'You can find our mobile terms and privacy policy online at {clinic_website}. ';

/**
 * Render the verbal disclosure. Must produce byte-identical output to the backend
 * SmsConsentDisclosure::render() for the same inputs.
 */
export function renderSmsConsentDisclosure(clinicName, clinicWebsite) {
    const template = clinicWebsite ? SMS_CONSENT_DISCLOSURE_TEMPLATE : SMS_CONSENT_DISCLOSURE_TEMPLATE.replace(SMS_CONSENT_WEBSITE_SENTENCE, '');
    return template.replace('{clinic_name}', clinicName || 'This clinic').replace('{clinic_website}', clinicWebsite || '');
}

/**
 * Self-service disclosure shown above the digital consent widget's checkboxes (#4585 FR-6, #5235).
 * Distinct from the verbal script above — this wording is written for a patient filling out a form,
 * and carries every element TCR requires of an opt-in path (sender identity, message types,
 * frequency, rates, STOP, HELP, privacy/terms reference) because TwilioService::campaignContent()
 * submits it to TCR as the description of this path.
 *
 * Must stay byte-identical to App\Constants\SmsConsentDisclosure::DIGITAL_FORM_TEXT; the backend
 * stamps that exact wording as the event's disclosure snapshot for digital-form captures.
 */
export const SMS_CONSENT_DIGITAL_DISCLOSURE =
    'I consent to receive text messages from this practice at the number(s) below, including ' +
    'appointment reminders, clinical messages from my care team, and links to complete my paperwork. ' +
    'Message frequency varies. Message and data rates may apply. Reply STOP at any time to opt out. ' +
    "Reply HELP for help. This practice's SMS privacy policy and SMS terms and conditions apply.";

/**
 * Derive the consent status for a fhirworks ContactPoint. The status lives on the
 * first-class smsReminderConsent field (legacy-fce); absent ⇒ not-asked. A new or changed
 * number therefore reads as not-asked automatically (FR-10).
 */
export function getConsentStatus(contactPoint) {
    return contactPoint?.smsReminderConsent?.status || SMS_CONSENT_STATUS.NOT_ASKED;
}

/**
 * The patient's mobile phone ContactPoint, or null when none is on file. Consent is scoped
 * to the mobile number, so chip placements derive their state from this entry.
 */
export function getMobileContactPoint(resource) {
    const telecom = resource?.telecom || [];
    return telecom.find((cp) => cp?.system === 'phone' && cp?.use === 'mobile') || null;
}
